import React, { useState, useRef, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import './_tags-input.scss';
import './_tags-input.dark-mode.scss';
import classNames from 'classnames';
import { truncate } from 'lodash';

const TagsInput = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [inputValue, setInputValue] = useState('');
  const [tags, setTags] = useState(value ? value.split(',') : []);
  const [activeFocus, setActiveFocus] = useState(false);

  const removeTag = (index) => {
    const newTags = [...tags];
    const filteredTags = newTags.filter((tag, i) => i !== index);
    setTags(filteredTags);
    inputRef.current.focus();
  };

  const inputKeyDown = (e, isBlur) => {
    if ((e.key === 'Enter' || e.keyCode === 188 || e.key === ',' || isBlur) && inputValue && !/^ *$/.test(inputValue)) {
      if (tags.find((tag) => inputValue.toLowerCase().trim().split(',').includes(tag.toLowerCase()))) {
        return setInputValue('');
      }

      const addedTags = [
        ...tags,
        ...inputValue
          .split(',')
          .map((substr) => substr.trim())
          .filter((tag) => !!tag),
      ];
      setTags(addedTags);
      setInputValue('');
      setTimeout(() => {
        listRef.current?.scrollTo({ top: 9999 });
      }, 150);
    } else if (e.key === 'Backspace' && !inputValue) {
      removeTag(tags.length - 1);
    }

    return false;
  };

  const onChangeInput = (e) => {
    if (e.target.value === ',') {
      return setInputValue('');
    }
    return setInputValue(e.target.value);
  };

  const onClickField = (e) => {
    e.preventDefault();
    if (e.target.classList.contains('input-tag') || e.target.classList.contains('input-tag__tags')) {
      listRef.current?.scrollTo({ top: 9999 });
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    listRef.current?.scrollTo({ top: 9999 });
  }, []);

  useEffect(() => {
    const tagsToString = tags.join(', ').trimEnd();
    if (onChange && tagsToString !== value) {
      onChange(tagsToString)
    }
  }, [onChange, tags, value])

  return (
    <div
      className={classNames('input-tag', {
        'input-tag__focus': activeFocus,
        'input-tag__empty': !tags.length,
      })}
      onClick={(e) => onClickField(e)}
    >
      <ul className="input-tag__tags" ref={listRef}>
        {tags.map((tag, i) => (
          <li
            title={tag}
            key={i}
            className={classNames({
              'new-tag': !value.includes(tag),
            })}
          >
            {truncate(tag, {
              length: 90,
              omission: '...',
            })}
            <button type="button" onClick={() => removeTag(i)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M17.613 6.387a1.323 1.323 0 0 1 0 1.871L13.87 12l3.742 3.742a1.323 1.323 0 0 1-1.871 1.87L12 13.872l-3.742 3.742a1.323 1.323 0 0 1-1.87-1.871L10.128 12 6.387 8.258a1.323 1.323 0 1 1 1.871-1.87L12 10.128l3.742-3.742a1.323 1.323 0 0 1 1.87 0z" />
              </svg>
            </button>
          </li>
        ))}
        <li className="input-tag__tags-input">
          <input
            type="text"
            value={inputValue}
            onChange={onChangeInput}
            onKeyDown={inputKeyDown}
            ref={inputRef}
            onFocus={() => setActiveFocus(true)}
            onClick={() => setActiveFocus(true)}
            onBlur={(e) => {
              inputKeyDown(e, true)
              setActiveFocus(false)
            }}
          />
        </li>
      </ul>
    </div>
  );
};

TagsInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
};

export default memo(TagsInput, () => true);
